import startCase from "lodash/startCase";
import dynamic from "next/dynamic";
// eslint-disable-next-line no-restricted-imports
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import snakecaseKeys from "snakecase-keys";
import LoadingOverlay from "rivals/components/app/Layout/LoadingOverlay";
import Benji from "rivals/components/shared/Ads/Benji";
import {
  TOP_CENTER,
  TOP_RIGHT
} from "rivals/components/shared/Ads/Benji/constants";
import Comscore from "rivals/components/shared/Ads/Comscore";
import DistroScale from "rivals/components/shared/Ads/DistroScale/script";
import E2E from "rivals/components/shared/Ads/E2E";
import LREC from "rivals/components/shared/Ads/LREC";
import AdWrapper from "rivals/components/shared/Ads/WithRedux/container";
import RivalsButton from "rivals/components/shared/Button";
import CollegeLogo from "rivals/components/shared/CollegeLogo";
import Head from "rivals/components/shared/Head";
import Link from "rivals/components/shared/Link";
import ListTable from "rivals/components/shared/ListTable";
import PageToggle from "rivals/components/shared/PageToggle";
import { TOGGLE_VARIANTS } from "rivals/components/shared/PageToggle/types";
import RecentTransfers from "rivals/components/shared/RecentTransfers";
import RivalsListHeader from "rivals/components/shared/RivalsListHeader";
import YearsDropdown from "rivals/components/shared/YearsDropdown";
import PageWithTopSection from "rivals/components/templates/PageWithTopSection";
import { sendGAEvent } from "rivals/redux/ga/actions";
import { fetchListData, getContentListPeople } from "rivals/redux/list/actions";
import {
  getAvailableYears,
  getPeopleList,
  getPeopleListCollegeFocus,
  getPeopleListSuccess,
  getSubmitting,
  getTotalPeople,
  selectCurrentVersion,
  selectLatestVersion
} from "rivals/redux/list/selectors";
import { getPositions } from "rivals/redux/position/actions";
import { getTransfers } from "rivals/redux/transfer/actions";
import { getCurrentUser } from "rivals/redux/user/selectors";
import { HOST_URL } from "rivals/services/base";
import {
  LAZY_LOADING_MORE_SIZE,
  LAZY_LOADING_PAGE_SIZE,
  PATHS,
  RIVALS_LIST_SPORTS,
  TEAM_RANKING_TYPES
} from "rivals/shared/constants";
import { PeopleList, Version } from "rivals/shared/interfaces/List";
import { PositionType } from "rivals/shared/interfaces/Position";
import { ListKinds } from "rivals/shared/interfaces/adminLists";
import sharedStrings from "rivals/shared/strings/common";
import { useAppDispatch, useMountedState } from "rivals/shared/utils/hooks";
import {
  genShallowPushToURL,
  makeShareableListUrl
} from "rivals/shared/utils/urlUtils";
import PeopleFilters from "./PeopleFilters";
import {
  setDefaultFilters,
  setTransferTrackerPathVariables
} from "./constants";
import styles from "./index.module.scss";
import strings from "./strings";
import { OwnProps, PEOPLE_FILTERS_TYPES, PeopleFiltersType } from "./types";

const DistroScaleAd = dynamic(
  () => import("rivals/components/shared/Ads/DistroScale/container"),
  {
    ssr: false
  }
);

const { PageHeader } = PageWithTopSection;
const { ADMIN_EDIT_TP_RANKING_LIST_SHEETS, TRANSFER_TRACKER } = PATHS;
const {
  editRankList,
  loadMoreText,
  metaData,
  philter,
  topAthletes,
  transferRanking
} = strings;

const TransferTracker = ({
  list,
  schoolFocus,
  sport,
  urlFilters,
  versions
}: OwnProps): JSX.Element => {
  const isMounted = useMountedState();
  const router = useRouter();
  const [applyFilter, setApplyFilter] = useState<boolean>(false);
  const [changingSport, setChangingSport] = useState<boolean>(false);
  const [filters, setFilters] = useState<PeopleFiltersType>(
    urlFilters || setDefaultFilters()
  );
  const peopleListSuccess = useSelector(getPeopleListSuccess);
  const submitting = useSelector(getSubmitting);
  const people = useSelector(getPeopleList);
  const totalPeople = useSelector(getTotalPeople);
  const [filteredPeople, setFilteredPeople] = useState<PeopleList>(people);
  const user = useSelector(getCurrentUser);
  const availableTransferYears = useSelector(getAvailableYears);
  const peopleListCollegeFocus = useSelector(getPeopleListCollegeFocus);
  const currentVersion = useSelector(selectCurrentVersion);
  const latestVersion = useSelector(selectLatestVersion);

  const dispatch = useAppDispatch();
  const [selectedSportsTab, setSelectedSportsTab] = useState(
    sport || RIVALS_LIST_SPORTS.FOOTBALL
  );

  const userIsAdmin = user && user.admin;
  const allowSportToggle =
    availableTransferYears.football &&
    availableTransferYears.football.length > 0 &&
    availableTransferYears.basketball &&
    availableTransferYears.basketball.length > 0;

  const availableDropdownYears =
    list &&
    [RIVALS_LIST_SPORTS.BASKETBALL, RIVALS_LIST_SPORTS.FOOTBALL].includes(
      list.sport
    )
      ? availableTransferYears[list.sport]
      : [];

  const redirectSingleParamUrl = (year: string, sport: string): void => {
    const split = router.asPath.split("?");
    const newUrl = `${setTransferTrackerPathVariables(year, sport)}${
      split.length > 1 ? `?${split[1]}` : ""
    }`;
    router.replace(newUrl);
  };

  const onTabChange = (): void => {
    if (!submitting && list && allowSportToggle) {
      setChangingSport(true);
      const sport =
        selectedSportsTab === RIVALS_LIST_SPORTS.FOOTBALL
          ? RIVALS_LIST_SPORTS.BASKETBALL
          : RIVALS_LIST_SPORTS.FOOTBALL;

      const availableDispatchYears =
        (selectedSportsTab === RIVALS_LIST_SPORTS.FOOTBALL
          ? availableTransferYears.basketball
          : availableTransferYears.football) || [];

      // if the sport doesn't have an equivalent year, take the latest year instead
      const dispatchYear = availableDispatchYears.includes(list.year)
        ? list.year
        : availableDispatchYears[availableDispatchYears.length - 1];
      setSelectedSportsTab(sport);
      dispatch(
        getPositions({
          sport: sport,
          type: PositionType.collegePlayer,
          year: dispatchYear
        })
      );
      dispatch(
        fetchListData({
          kind: ListKinds.TRANSFER_PORTAL,
          sport: sport.toString(),
          year: dispatchYear
        })
      );
      dispatch(
        getContentListPeople(
          {
            college: schoolFocus || "",
            kind: ListKinds.TRANSFER_PORTAL,
            size: LAZY_LOADING_PAGE_SIZE,
            sport: sport.toString(),
            start: 1,
            year: dispatchYear
          },
          true
        )
      );
      dispatch(
        getTransfers({
          college: schoolFocus || "",
          isWidget: true,
          sport: sport.toString()
        })
      );
      setFilters(setDefaultFilters());
      genShallowPushToURL(
        TRANSFER_TRACKER,
        { sport: sport.toString(), year: dispatchYear.toString() },
        {
          ...{ schoolFocus: schoolFocus || "" },
          ...setDefaultFilters()
        }
      );
    }
  };

  useEffect(() => {
    if (list && router.query.slug.length == 1) {
      redirectSingleParamUrl(list.year.toString(), selectedSportsTab);
    }
    // To be reenabled with https://griosf.atlassian.net/browse/RVLS-10292
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, router.query.slug.length, selectedSportsTab]);

  useEffect(() => {
    // Update filteredPeople after api call
    if (peopleListSuccess) {
      setFilteredPeople(people);
    }
    if (changingSport) {
      setChangingSport(false);
    }
  }, [changingSport, people, peopleListSuccess]);

  useEffect(() => {
    if (list && applyFilter && !changingSport) {
      // Make api call with filter changes
      getPeople(list.listId, 1, LAZY_LOADING_PAGE_SIZE, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilter, changingSport, filters, list]);

  const getPeople = (
    listId: number,
    start: number,
    pageSize: number,
    resetting: boolean
  ): void => {
    dispatch(
      getContentListPeople(
        {
          athleticYear: filters[PEOPLE_FILTERS_TYPES.ATHLETIC_YEAR],
          college: schoolFocus || "",
          exitCollegeName: filters[PEOPLE_FILTERS_TYPES.EXIT_COLLEGE],
          id: listId,
          originCollegeName: filters[PEOPLE_FILTERS_TYPES.ORIGIN_COLLEGE],
          size: pageSize,
          start: start,
          transferPosition: filters[PEOPLE_FILTERS_TYPES.TRANSFER_POSITION]
        },
        resetting
      )
    );
  };

  const loadMore = (): void => {
    if (list) {
      const lastPersonOrdinality = people[people.length - 1]?.ordinality || 0;
      getPeople(
        list.listId,
        lastPersonOrdinality + 1,
        LAZY_LOADING_MORE_SIZE,
        false
      );
    }
  };

  const clearFilters = (): void => {
    setFilters(setDefaultFilters());
    if (list) {
      genShallowPushToURL(
        PATHS.TRANSFER_TRACKER,
        { sport: list.sport, year: list.year.toString() },
        {
          ...setDefaultFilters()
        }
      );
    }
  };

  const handleVersionSelect = useCallback(({ listId }: Version) => {
    dispatch(fetchListData({ id: listId }));
    getPeople(listId, 1, LAZY_LOADING_PAGE_SIZE, true);
    clearFilters();
    // To be re-enabled with https://griosf.atlassian.net/browse/RVLS-10292
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (list && list.current === false) {
      dispatch(
        sendGAEvent({
          eventName: "select_content",
          eventParams: {
            contentId: `${list.name}/version=${list.version}`,
            contentType: "transfer tracker ranking list"
          }
        })
      );
    }
  }, [dispatch, list]);

  const handleTransferYearSelect = (value: string): void => {
    const paramKeys = schoolFocus ? { schoolFocus: schoolFocus } : {};

    router.push({
      pathname: `${setTransferTrackerPathVariables(value, selectedSportsTab)}`,
      search: new URLSearchParams({
        ...snakecaseKeys(paramKeys)
      }).toString()
    });
    clearFilters();
  };

  let titleEnding = "transfer portal activity tracker";
  let titleMiddle = "transfer";
  if (filters.originCollegeName) {
    if (filters.exitCollegeName) {
      titleEnding = `transfers from ${startCase(
        filters.originCollegeName
      )} to ${startCase(filters.exitCollegeName)}`;
    } else {
      titleEnding = `transfers from ${startCase(filters.originCollegeName)}`;
    }
  } else if (filters.exitCollegeName) {
    titleEnding = `transfers to ${startCase(filters.exitCollegeName)}`;
  }

  if (filters.transferPosition) {
    titleMiddle = `${filters.transferPosition.toUpperCase()} transfer`;
  }

  const pageMetaTitle = metaData.title
    .replace("[year]", list.year.toString())
    .replace("[sport]", selectedSportsTab)
    .replace("transfer", titleMiddle)
    .replace("transfer portal activity tracker", titleEnding);

  const sportToggle = allowSportToggle ? (
    <PageToggle
      items={sharedStrings.sportsToggleTabs}
      onToggle={onTabChange}
      selected={selectedSportsTab}
      variant={TOGGLE_VARIANTS.SPORTS}
    />
  ) : undefined;

  const headerLink = peopleListCollegeFocus ? (
    <Link
      className={styles.nationalTrackerLink}
      data={`${philter}-national-tracker`}
      href={PATHS.TRANSFER_TRACKER.replace(
        "[year]",
        list.year.toString()
      ).replace("[sport]", list.sport.toString())}
    >
      {strings.viewNationalTracker}
    </Link>
  ) : undefined;

  const headerImage = peopleListCollegeFocus ? (
    <CollegeLogo
      collegeLogo={peopleListCollegeFocus?.largeTeamLogo}
      collegeName={peopleListCollegeFocus?.commonName}
      collegeSiteColor={peopleListCollegeFocus?.color}
      logoSize={80}
    />
  ) : undefined;

  const rivalsHeaderListTitle = useMemo(() => {
    return peopleListCollegeFocus?.commonName
      ? `${peopleListCollegeFocus.commonName} ${transferRanking}`
      : transferRanking;
  }, [peopleListCollegeFocus]);

  const socialMediaUrl = useMemo(() => {
    if (!isMounted || !list) {
      return;
    }
    return makeShareableListUrl(list);
  }, [isMounted, list]);

  return (
    <>
      <Head
        description={metaData.description}
        ogImage={metaData.imageUrl}
        ogImageAlt={metaData.imageAlt}
        ogUrl={`${HOST_URL}${setTransferTrackerPathVariables(
          list.year.toString(),
          list.sport
        )}`}
        title={pageMetaTitle}
        twitterImage={metaData.imageUrl}
        twitterTitle={pageMetaTitle}
      />
      <Benji />
      <Comscore />
      <DistroScale />
      <PageWithTopSection
        data={philter}
        topSection={
          <PageHeader
            ad={
              <AdWrapper
                ad={E2E}
                location={TOP_CENTER}
                locationCount={1}
                slotNumber={1}
              />
            }
            headerColor={peopleListCollegeFocus?.color}
            headerImage={headerImage}
            headerLink={headerLink}
            pageTitle={strings.pageTitle}
            secondLevelRightSection={sportToggle}
            subtitle={peopleListCollegeFocus?.commonName}
          />
        }
      >
        <RecentTransfers data={philter} />
        <div className={styles.mainContentWrapper}>
          <div className={styles.leftColumnWrapper}>
            {userIsAdmin && (
              <div className={styles.viewTransferPortalPlayersLinkWrapper}>
                <Link
                  className={styles.editRankListLink}
                  data={`${philter}-admin-edit-rank-list`}
                  href={ADMIN_EDIT_TP_RANKING_LIST_SHEETS.replace(
                    "[sport]",
                    `${list.sport}`
                  ).replace("[year]", `${list.year}`)}
                >
                  {editRankList(list.sport || "")}
                </Link>
              </div>
            )}

            <RivalsListHeader
              currentVersion={currentVersion}
              latestVersion={latestVersion}
              onSelectVersion={handleVersionSelect}
              socialMediaPostText={strings.socialMediaPostText}
              socialMediaUrl={socialMediaUrl}
              subtitle={topAthletes}
              title={rivalsHeaderListTitle}
              titleColor={styles["active-blue"]}
              type={TEAM_RANKING_TYPES.TRANSFER}
              versions={versions}
            />
            <div className={styles.listFiltersWrapper}>
              <YearsDropdown
                availableYears={availableDropdownYears || []}
                onSelect={handleTransferYearSelect}
                philter={`${philter}-transfer-years`}
                year={list.year}
              />
              <PeopleFilters
                applyFilters={setApplyFilter}
                data={philter}
                filters={filters}
                onChange={setFilters}
                sport={list.sport}
                year={list.year}
              />
            </div>
            <div className={styles.listWrapper}>
              <ListTable
                filters={filters}
                list={list}
                people={filteredPeople}
                philter={philter}
              />
            </div>
            {totalPeople > filteredPeople.length && (
              <RivalsButton
                buttonText={loadMoreText}
                className={styles.showMoreButton}
                data={`${philter}-list-load-more`}
                disabled={submitting}
                inverted
                onClick={loadMore}
                size="small"
              />
            )}
          </div>
          <div className={styles.rightColumnWrapper}>
            <AdWrapper
              ad={LREC}
              location={TOP_RIGHT}
              locationCount={1}
              slotNumber={1}
            />
            <div className={styles.distroScaleContainer}>
              <DistroScaleAd />
            </div>
          </div>
        </div>
      </PageWithTopSection>
      <LoadingOverlay force={submitting} />
    </>
  );
};

export default TransferTracker;
